import React, { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import Chat from "../ai/shipment-chat/chat/chat";
import { ChatMessageModel } from "../../app/data/chat/models";
import SlideOutSidebar from "../../ui-components/slide-out-sidebar/slideOutSidebar";
import ImportantMessage, { ImportantMessageTheme } from "../../ui-components/molecules/important-message/importantMessage";
import { apiQueryAdapter } from "../../app/data/apiQueryAdapter";
import "./customer-chat.scss";

const INITIAL_MESSAGES = [
  `Welcome! I can help you with your shipments, tracking, and any questions you might have. Feel free to ask me anything about your account or shipments.`,
  `AI can make mistakes. AI-generated responses rely on data and algorithms, which means they may not always be accurate or comprehensive.
   Users should independently verify information and consider AI insights as supplementary to expert advice.`,
];

const MESSAGE_DELAY = 500;

const chatWithCustomer = async ({ message, chatHistory }: { message: string, chatHistory: any[] }) => {
  const response = await apiQueryAdapter.query("/customer/chat", "POST", {
    body: {
      user_prompt: message,
      chat_history: chatHistory
    }
  });
  return response.data;
};

export const CustomerChat = ({ show, onClose }: { show: boolean, onClose: () => void }) => {
  const [messages, setMessages] = useState<ChatMessageModel[]>([]);
  const [chatHistory, setChatHistory] = useState<any[]>([]);
  const [isChatError, setIsChatError] = useState(false);

  const chatMutation = useMutation({
    mutationFn: chatWithCustomer,
    onSuccess: (data) => {
      setChatHistory(data.chat);

      setMessages(prev => [...prev, { 
        text: data.response, 
        incoming: true
      }]);
    },
    onError: (error) => {
      console.error("Error sending message:", error);
      setIsChatError(true);
    }
  });

  const handleChatSend = (message: ChatMessageModel) => {
    setIsChatError(false);
    setMessages(prev => [...prev, message]);
    chatMutation.mutate({
      message: message.text!,
      chatHistory,
    });
  };

  const handleChatRetry = () => {
    setIsChatError(false);
    let message = messages[messages.length - 1]
    chatMutation.mutate({
      message: message.text!,
      chatHistory,
    })
  }

  useEffect(() => {
    let timeouts: any[] = [];
    if (show && messages.length < INITIAL_MESSAGES.length) {
      INITIAL_MESSAGES.slice(messages.length).forEach((message, i) => {
        const timeout = setTimeout(() => {
          setMessages(prev => [...prev, { incoming: true, text: message }]);
        }, MESSAGE_DELAY * (i + 1));
        timeouts.push(timeout);
      });
    }
    return () => timeouts.forEach(clearTimeout);
  }, [messages, show]);

  return (
    <div className="customer-chat-wrapper">
      <SlideOutSidebar
        header="Customer Assistant"
        show={show}
        onClose={onClose}
        size="medium"
        className="customer-chat-sidebar"
      >
        <div className="customer-chat">
          <ImportantMessage theme={ImportantMessageTheme.BLUE} mix="customer-chat__disclaimer">
            AI can make mistakes. Users should independently verify information.
          </ImportantMessage>

          <Chat
            mix="customer-chat__chat"
            messages={messages}
            onSend={handleChatSend}
            isLoading={chatMutation.isLoading}
            isDisabled={false}
            animated={true}
            isError={isChatError}
            onRetry={handleChatRetry}
          />
        </div>
      </SlideOutSidebar>
    </div>
  );
};

export default CustomerChat; 